import React, { lazy, Suspense } from 'react';
import { RouteObject } from 'react-router';
import { Navigate } from 'react-router-dom';
import Authenticated from 'src/components/Authenticated';
import RoleProtectedRoute from 'src/components/RoleProtectedRoute';
import SuspenseLoader from 'src/components/SuspenseLoader';
import Home from 'src/content/dashboards/home';
import ApiDocsPage from 'src/content/pages/ApiDocs';
import FlowCustom from 'src/content/pages/FlowCustom';
import Builder from 'src/content/pages/Flows/Builder';
import FlowConfiguration from 'src/content/pages/Flows/Configuration';
import NewFlowWizard from 'src/content/pages/Flows/NewFlowWizard';
import FlowList from 'src/content/pages/Flows/List';
import FlowManagementList from 'src/content/pages/Flows/FlowManagement';
import Rules from 'src/content/pages/Flows/Rules';
import FlowView from 'src/content/pages/Flows/View';
import Results from 'src/content/pages/Sessions/Results';
import BaseLayout from 'src/layouts/BaseLayout';
import ShadcnSidebar from 'src/layouts/ShadcnSidebar';

import baseRoutes from './base';

const Loader = (Component: React.ComponentType<any>) => (props: any) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

const Callback = Loader(lazy(() => import('src/content/Auth/Callback')));

const NewSessionDetails = Loader(
  lazy(() => import('src/content/pages/Transactions/newSessionDetails')),
);

const Statistics = Loader(lazy(() => import('src/content/pages/Statistics')));

const Login = Loader(lazy(() => import('src/content/Auth/Login')));

const Users = Loader(lazy(() => import('src/content/pages/Users')));

const UserProfile = Loader(lazy(() => import('src/content/pages/UserProfile')));

const TeamManagement = Loader(
  lazy(() => import('src/content/pages/Users/TeamManagement')),
);

const Permissions = Loader(
  lazy(() => import('src/content/pages/Users/Permissions')),
);

const LoginHistory = Loader(
  lazy(() => import('src/content/pages/Users/LoginHistory')),
);

const Organizations = Loader(
  lazy(() => import('src/content/pages/Organizations')),
);

const Betclic = Loader(lazy(() => import('src/content/pages/Betclic/Index')));

const DevelopersIndex = Loader(
  lazy(() => import('src/content/pages/Developers')),
);
const DevelopersApiKeys = Loader(
  lazy(() => import('src/content/pages/Developers/ApiKeys')),
);
const DevelopersWebhooks = Loader(
  lazy(() => import('src/content/pages/Developers/Webhooks')),
);
const DevelopersSessions = Loader(
  lazy(() => import('src/content/pages/Developers/Sessions')),
);
const DevelopersDocs = Loader(
  lazy(() => import('src/content/pages/Developers/Docs')),
);
const DevelopersStatus = Loader(
  lazy(() => import('src/content/pages/Developers/Status')),
);

const router: RouteObject[] = [
  {
    path: '',
    element: (
      <Authenticated>
        <ShadcnSidebar />
      </Authenticated>
    ),
    children: [
      {
        path: '/',
        element: (
          <RoleProtectedRoute subject="Home" action="read">
            <Home />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/home',
        element: (
          <RoleProtectedRoute subject="Home" action="read">
            <Home />
          </RoleProtectedRoute>
        ),
      },
      {
        path: `/transactions/sessions/:transactionId`,
        element: (
          <RoleProtectedRoute subject="Sessions" action="read">
            <NewSessionDetails />
          </RoleProtectedRoute>
        ),
      },
      {
        path: `/transactions/sessions`,
        element: (
          <RoleProtectedRoute subject="Sessions" action="read">
            <Results />
          </RoleProtectedRoute>
        ),
      },
      {
        path: `/transactions/statistics`,
        element: (
          <RoleProtectedRoute subject="Sessions" action="read">
            <Statistics />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/users',
        element: (
          <RoleProtectedRoute subject="Users" action="read">
            <Users />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/users/:userId/login-history',
        element: (
          <RoleProtectedRoute subject="Users" action="read">
            <LoginHistory />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/user/profile',
        element: (
          <RoleProtectedRoute subject="Profile" action="read">
            <UserProfile />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/users/team-management',
        element: (
          <RoleProtectedRoute subject="Users" action="read">
            <TeamManagement />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/permissions',
        element: (
          <RoleProtectedRoute subject="Users" action="read">
            <Permissions />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/organizations',
        element: (
          <RoleProtectedRoute subject="Organizations" action="read">
            <Organizations />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/flows/list',
        element: (
          <RoleProtectedRoute subject="Flows" action="read" requireAdmin>
            <FlowList />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/flows/flow-list',
        element: (
          <RoleProtectedRoute subject="Flows" action="read">
            <FlowManagementList />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/flows/builder',
        element: (
          <RoleProtectedRoute subject="Flows" action="read" requireAdmin>
            <Builder />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/flows/builder/:id',
        element: (
          <RoleProtectedRoute subject="Flows" action="read" requireAdmin>
            <Builder />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/flows/view/:id',
        element: (
          <RoleProtectedRoute subject="Flows" action="read" requireAdmin>
            <FlowView />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/flows/rules',
        element: (
          <RoleProtectedRoute subject="Flows" action="read" requireAdmin>
            <Rules />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/flows/new',
        element: (
          <RoleProtectedRoute subject="Flows" action="read" requireAdmin>
            <NewFlowWizard />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/flows/configuration',
        element: (
          <RoleProtectedRoute subject="Flows" action="read" requireAdmin>
            <FlowConfiguration />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/flows/custom',
        element: (
          <RoleProtectedRoute subject="Flows" action="read" requireAdmin>
            <FlowCustom />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/id-upload',
        element: (
          <RoleProtectedRoute subject="IdUpload" action="read">
            <Betclic />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/betclic',
        element: <Navigate to="/id-upload" replace />,
      },
      {
        path: '/developers',
        element: (
          <RoleProtectedRoute subject="Developers" action="read">
            <DevelopersIndex />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/developers/api-keys',
        element: (
          <RoleProtectedRoute subject="DevHubApiKeys" action="read">
            <DevelopersApiKeys />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/developers/webhooks',
        element: (
          <RoleProtectedRoute subject="DevHubWebhooks" action="read">
            <DevelopersWebhooks />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/developers/sessions',
        element: (
          <RoleProtectedRoute subject="DevHubSessionExpiry" action="read">
            <DevelopersSessions />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/developers/docs',
        element: (
          <RoleProtectedRoute subject="DevHubDocumentation" action="read">
            <DevelopersDocs />
          </RoleProtectedRoute>
        ),
      },
      {
        path: '/developers/status',
        element: (
          <RoleProtectedRoute subject="DevHubStatus" action="read">
            <DevelopersStatus />
          </RoleProtectedRoute>
        ),
      },
    ],
  },

  {
    path: 'status',
    element: (
      <Authenticated>
        <BaseLayout />
      </Authenticated>
    ),
    children: baseRoutes,
  },
  {
    path: '/api-docs',
    element: (
      <Authenticated>
        <RoleProtectedRoute subject="Home" action="read">
          <ApiDocsPage />
        </RoleProtectedRoute>
      </Authenticated>
    ),
  },
  { path: '/login', element: <Login /> },
  { path: '/callback', element: <Callback /> },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
];

export default router;
